import { Container, IDestroyOptions } from "@pixi/display";
import { FigureData } from "../../../models/figure/FigureData";
import { SquareView } from "../square/SquareView";
import { Event } from "event-system";
import { FederatedPointerEvent } from "@pixi/events";
import { BaseFigureView } from "./BaseFigureView";
import { gsap } from "gsap/gsap-core";
import { Point } from "@pixi/core";
import { TriangleView } from "../triangles/TriangleView";
type Timeline = gsap.core.Timeline;

export class DeckFigureView extends BaseFigureView {
    public onSelected: Event<DeckFigureView, FederatedPointerEvent>;
    private showAnimation: Timeline;
    private readonly startingAnimation: Timeline;
    private readonly revertAnimation: Timeline;
    private readonly deckFigureScale: number;

    public constructor(
        figureData: FigureData,
        squares: Array<SquareView>,
    ) {
        super(figureData, squares);
        this.onSelected = new Event<DeckFigureView, FederatedPointerEvent>();
        this.eventMode = "static";
        this.deckFigureScale = 0.864;
        this.startingAnimation = new gsap.core.Timeline();
        this.revertAnimation = new gsap.core.Timeline();
        this.on("pointerdown", this.onSelectFigure, this);
        this.scale.set(this.deckFigureScale);
    }

    private onSelectFigure(event: FederatedPointerEvent): void {
        this.onSelected.emit(this, event);
    }

    public async playShowAnimation(): Promise<void> {
        this.showAnimation?.clear();
        this.showAnimation = new gsap.core.Timeline();
        this.eventMode = "none";
        const startPosition = this.position.clone();
        const width = this.width;
        const height = this.height;
        await this.showAnimation
            .fromTo(this.scale,
                {
                    x: 0,
                    y: 0,
                },
                {
                    x: this.deckFigureScale,
                    y: this.deckFigureScale,
                    ease: "power2.in",
                    duration: 0.3
                })
            .fromTo(this.position,
                {
                    x: startPosition.x + width * 0.5,
                    y: startPosition.y + height * 0.5
                },
                {
                    x: startPosition.x + (width - width * this.deckFigureScale) * 0.5,
                    y: startPosition.y + (height - height * this.deckFigureScale) * 0.5,
                    ease: "power2.in",
                    duration: 0.3
                }, "<");
        const triangles = this.findAllTriangles(this);
        const animations = triangles.map(triangle => triangle.deckSpawnAnimation());
        await Promise.all(animations);
        this.eventMode = "static";
    }

    public async playStartingAnimation(endPosition: Point): Promise<void> {
        this.startingAnimation?.clear();
        await this.startingAnimation
            .fromTo(this.scale,
                {
                    x: this.deckFigureScale,
                    y: this.deckFigureScale
                },
                {
                    x: 1,
                    y: 1,
                    duration: 0.1
                })
            .to(this,
                {
                    x: endPosition.x,
                    y: endPosition.y,
                    duration: 0.1
                }, "<");
    }

    public async playRevertAnimation(endPosition: Point): Promise<void> {
        this.revertAnimation?.clear();
        await this.revertAnimation
            .fromTo(this.scale,
                {
                    x: 1,
                    y: 1
                },
                {
                    x: this.deckFigureScale,
                    y: this.deckFigureScale,
                    duration: 0.1
                })
            .to(this,
                {
                    x: endPosition.x,
                    y: endPosition.y,
                    duration: 0.1
                }, "<");
    }

    private findAllTriangles(container: Container): Array<TriangleView> {
        let triangles = new Array<TriangleView>();
        for (let child of container.children) {
            if (child instanceof TriangleView) {
                triangles.push(child);
            }
            if (child instanceof Container) {
                triangles = triangles.concat(this.findAllTriangles(child));
            }
        }
        return triangles;
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.startingAnimation?.kill();
        this.revertAnimation?.kill();
        this.showAnimation?.kill();
        this.off("pointerdown", this.onSelectFigure, this);
        super.destroy(options);
    }
}
